import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useTranslation } from "react-i18next";
import { Flashlight, FlashlightOff } from "lucide-react";

const READER_ID = "loyalty-qr-reader";

interface Props {
  /** Drives camera start/stop — true while the scan screen is open. The
   *  stream stays alive the whole time the screen is open (permission is
   *  requested once); use `paused` to stop decoding without releasing it. */
  isOpen: boolean;
  /** Pause decoding without tearing down the camera (keeps the permission
   *  + stream alive). Used while the user's own QR sheet is expanded. */
  paused?: boolean;
  /** Called with the decoded text on a successful scan. */
  onScanned: (text: string) => void;
  /** Extra classes for the camera box (e.g. full-screen sizing). */
  className?: string;
}

/** In-app QR scanner (html5-qrcode). Always opens the rear camera
 *  ("environment" prefers it and auto-falls back to the front camera on
 *  devices without a rear one — no manual switch). Torch toggle where the
 *  device supports it. Camera permission is requested once by the browser
 *  and remembered; the stream is fully stopped on unmount / close. */
export default function LoyaltyScanner({
  isOpen,
  paused = false,
  onScanned,
  className,
}: Props) {
  const { t } = useTranslation();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const startingRef = useRef(false);
  const onScannedRef = useRef(onScanned);
  onScannedRef.current = onScanned;
  // Soft-pause: keep the camera stream alive but ignore decoded results while
  // the user's own QR sheet is open. Avoids html5-qrcode's pause() — which
  // injects a "Scan paused" overlay — and a camera re-prompt from stop()/start().
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
    } catch {
      /* already stopped / never started */
    }
    setTorchOn(false);
  }, []);

  const start = useCallback(async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setError(null);
    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(READER_ID, false);
      }
      await scannerRef.current.start(
        // "environment" prefers the rear camera and auto-falls back to the
        // front camera on devices without one — so no manual switch needed.
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decodedText) => {
          if (!pausedRef.current) onScannedRef.current(decodedText);
        },
        () => {
          /* per-frame decode misses — ignore */
        }
      );
      try {
        const torch = scannerRef.current
          .getRunningTrackCameraCapabilities()
          .torchFeature();
        setTorchSupported(torch.isSupported());
      } catch {
        setTorchSupported(false);
      }
    } catch (err) {
      console.error("scanner start failed:", err);
      setError(
        t("camera_permission_denied") ||
          "Kameraga ruxsat berilmadi yoki kamera topilmadi"
      );
    } finally {
      startingRef.current = false;
    }
  }, [t]);

  // Start when the screen opens; stop + release the camera only when the
  // screen closes / unmounts. The stream is kept alive across QR-sheet
  // expand/collapse so the browser asks for camera permission just once.
  useEffect(() => {
    if (!isOpen) return;
    start();
    return () => {
      stop();
    };
  }, [isOpen, start, stop]);

  const toggleTorch = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      const torch = s.getRunningTrackCameraCapabilities().torchFeature();
      await torch.apply(!torchOn);
      setTorchOn((v) => !v);
    } catch (err) {
      console.error("torch toggle failed:", err);
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      {/* Recolour html5-qrcode's default white scan-box corner markers
          (#qr-shaded-region > div, inline #ffffff) to DON orange-100. */}
      <style>{`#qr-shaded-region > div { background-color: #FBC76C !important; }`}</style>
      <div
        className={
          className ||
          "relative w-full max-w-[280px] aspect-square rounded-2xl overflow-hidden bg-black"
        }
      >
        <div
          id={READER_ID}
          className="w-full h-full [&_video]:!w-full [&_video]:!h-full [&_video]:object-cover"
        />

        {error && (
          <div className="absolute inset-0 flex-center px-5 text-center text-xs text-gray-1 bg-gray-4">
            {error}
          </div>
        )}

        {!error && torchSupported && (
          <button
            type="button"
            onClick={toggleTorch}
            aria-label={t("flashlight") || "Fonar"}
            className={`absolute right-4 top-20 size-10 rounded-full backdrop-blur flex-center transition-colors ${
              torchOn
                ? "bg-orange-100 text-dark"
                : "bg-dark/60 text-white active:bg-dark/80"
            }`}
          >
            {torchOn ? (
              <FlashlightOff className="size-5" />
            ) : (
              <Flashlight className="size-5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
